import React from "react";
import { Animate } from "react-move";

import { easeBounceOut } from "@/utils/animation";
import base64 from "@/utils/base64";
import { classNameResolver } from "@/utils/transform";

import { centerPoint } from "../MeterConstants";

export type MeterVoteHandleProps = {
  icons: IIcons;
  answer?: number;
  voteHandle: number;
  voteHandleTouched: boolean;
  onMoveVoteHandle: (clientX: number, clientY: number) => void;
  onSnapVoteHandle: (clientX: number, clientY: number) => void;
  mobile?: boolean;
};

export default class MeterVoteHandle extends React.Component<
  MeterVoteHandleProps,
  {}
> {
  private startListener: any;
  private endListener: any;
  private moveListener: any;

  private voteHandleRef: any;

  constructor(props: any) {
    super(props);
    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.voteHandleRef = React.createRef<SVGGElement>();
  }

  public componentDidMount(): void {
    this.startListener = this.voteHandleRef.current.addEventListener(
      "touchstart",
      this.handleStart,
      {
        passive: false,
      }
    );
    this.endListener = this.voteHandleRef.current.addEventListener(
      "touchend",
      this.handleEnd,
      {
        passive: false,
      }
    );
    this.moveListener = this.voteHandleRef.current.addEventListener(
      "touchmove",
      this.handleMove,
      {
        passive: false,
      }
    );
  }

  public componentWillUnmount(): void {
    this.voteHandleRef.current.removeEventListener(
      "touchstart",
      this.handleStart
    );
    this.voteHandleRef.current.removeEventListener("touchend", this.handleEnd);
    this.voteHandleRef.current.removeEventListener(
      "touchmove",
      this.handleMove
    );
  }

  public render(): JSX.Element {
    const { icons, mobile, answer, voteHandle, voteHandleTouched } = this.props;
    const sliderScale = mobile ? 1.5 : 1;
    const sliderX = -((28 / 2) * sliderScale);
    const sliderY = -((47 / 2) * sliderScale);
    const sliderWidth = 28 * sliderScale;
    const sliderHeight = 47 * sliderScale;

    return (
      <Animate
        start={{
          x: voteHandle,
        }}
        update={
          voteHandleTouched
            ? {
                x: [voteHandle],
                timing: {
                  duration: 1,
                },
              }
            : {
                x: [voteHandle],
                timing: {
                  delay: 1,
                  duration: 300,
                  ease: easeBounceOut,
                },
              }
        }
      >
        {(animation: { x: number }) => (
          <g
            transform={`translate(${animation.x}, ${centerPoint.y})`}
            style={{ cursor: "pointer" }}
            ref={this.voteHandleRef}
            data-qa="meter:handle"
          >
            <g
              className={classNameResolver(["meterHandle"], {
                ["meterPulse"]: answer === undefined,
                ["meterHandleTouched"]: voteHandleTouched,
              })}
            >
              <image
                xlinkHref={
                  "data:image/svg+xml;base64," + base64.encode(icons.Slider)
                }
                x={sliderX}
                y={sliderY}
                width={sliderWidth}
                height={sliderHeight}
                onDragStart={(e: React.MouseEvent) => e.preventDefault()}
              />
            </g>
          </g>
        )}
      </Animate>
    );
  }

  private handleStart(e: React.TouchEvent): void {
    e.preventDefault();
    this.setState({ voteHandleTouched: true });
  }

  private handleMove(e: React.TouchEvent): void {
    e.preventDefault();
    this.props.onMoveVoteHandle(
      e.touches.item(0)!.clientX,
      e.touches.item(0)!.clientY
    );
  }

  private handleEnd(e: React.TouchEvent): void {
    e.preventDefault();
    const clientX = e.changedTouches.item(0)!.clientX;
    const clientY = e.changedTouches.item(0)!.clientY;
    this.setState({ voteHandleTouched: false }, () =>
      this.props.onSnapVoteHandle(clientX, clientY)
    );
  }
}
